import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  getAdminQuiz,
  updateQuiz,
  publishQuiz,
  unpublishQuiz,
  createQuiz,
  addQuestion,
  updateQuestion,
  deleteQuestion,
} from '../services/quizService';
import {
  QuizWithQuestions,
  UpdateQuizData,
  CreateQuizData,
  CreateQuizQuestionData,
  UpdateQuizQuestionData,
} from '../types/quiz';

interface QuizBuilderContextType {
  // State
  quiz: QuizWithQuestions | null;
  loading: boolean;
  error: string | null;
  successMessage: string | null;

  // Quiz operations
  loadQuiz: (quizId: number) => Promise<void>;
  createNewQuiz: (data: CreateQuizData) => Promise<QuizWithQuestions>;
  updateQuizDetails: (quizId: number, data: UpdateQuizData) => Promise<void>;
  togglePublishStatus: (quiz: QuizWithQuestions) => Promise<void>;

  // Question operations
  addQuestionToQuiz: (quizId: number, data: CreateQuizQuestionData) => Promise<void>;
  updateQuestionInQuiz: (quizId: number, questionId: number, data: UpdateQuizQuestionData) => Promise<void>;
  deleteQuestionFromQuiz: (quizId: number, questionId: number) => Promise<void>;

  // UI state management
  clearError: () => void;
  clearSuccessMessage: () => void;
  setError: (error: string) => void;
  setSuccessMessage: (message: string) => void;
}

const QuizBuilderContext = createContext<QuizBuilderContextType | undefined>(undefined);

interface QuizBuilderProviderProps {
  children: ReactNode;
}

export const QuizBuilderProvider: React.FC<QuizBuilderProviderProps> = ({ children }) => {
  const [quiz, setQuiz] = useState<QuizWithQuestions | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setErrorState] = useState<string | null>(null);
  const [successMessage, setSuccessMessageState] = useState<string | null>(null);

  const setError = useCallback((error: string) => {
    setErrorState(error);
  }, []);

  const setSuccessMessage = useCallback((message: string) => {
    setSuccessMessageState(message);
  }, []);

  const clearError = useCallback(() => {
    setErrorState(null);
  }, []);

  const clearSuccessMessage = useCallback(() => {
    setSuccessMessageState(null);
  }, []);

  const loadQuiz = useCallback(async (quizId: number) => {
    try {
      setLoading(true);
      setErrorState(null);
      const data = await getAdminQuiz(quizId);
      setQuiz(data);
    } catch (err: unknown) {
      console.error('Error loading quiz:', err);
      const error = err as { response?: { data?: { message?: string; error?: string } } };
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to load quiz';
      setErrorState(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const createNewQuiz = useCallback(async (data: CreateQuizData): Promise<QuizWithQuestions> => {
    try {
      setLoading(true);
      setErrorState(null);
      const newQuiz = await createQuiz(data);
      // Load the full quiz with questions after creation
      const fullQuiz = await getAdminQuiz(newQuiz.id);
      setQuiz(fullQuiz);
      setSuccessMessageState('Quiz created successfully!');
      return fullQuiz;
    } catch (err: unknown) {
      console.error('Error creating quiz:', err);
      const error = err as { response?: { data?: { message?: string; error?: string } } };
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to create quiz';
      setErrorState(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateQuizDetails = useCallback(async (quizId: number, data: UpdateQuizData) => {
    try {
      setLoading(true);
      setErrorState(null);
      await updateQuiz(quizId, data);
      // Reload the full quiz with questions after update
      const fullQuiz = await getAdminQuiz(quizId);
      setQuiz(fullQuiz);
      setSuccessMessageState('Quiz updated successfully!');
    } catch (err: unknown) {
      console.error('Error updating quiz:', err);
      const error = err as { response?: { data?: { message?: string; error?: string } } };
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to update quiz';
      setErrorState(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const togglePublishStatus = useCallback(async (currentQuiz: QuizWithQuestions) => {
    try {
      setLoading(true);
      setErrorState(null);

      if (currentQuiz.isPublished) {
        await unpublishQuiz(currentQuiz.id);
        setSuccessMessageState('Quiz unpublished successfully!');
      } else {
        await publishQuiz(currentQuiz.id);
        setSuccessMessageState('Quiz published successfully!');
      }

      // Reload quiz to get updated status
      await loadQuiz(currentQuiz.id);
    } catch (err: unknown) {
      console.error('Error toggling publish status:', err);
      const error = err as { response?: { data?: { message?: string; error?: string } } };
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to update quiz status';
      setErrorState(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [loadQuiz]);

  const addQuestionToQuiz = useCallback(async (quizId: number, data: CreateQuizQuestionData) => {
    try {
      setLoading(true);
      setErrorState(null);
      await addQuestion(quizId, data);
      setSuccessMessageState('Question added successfully!');
      // Reload quiz to get updated questions
      await loadQuiz(quizId);
    } catch (err: unknown) {
      console.error('Error adding question:', err);
      const error = err as { response?: { data?: { message?: string; error?: string } } };
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to add question';
      setErrorState(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [loadQuiz]);

  const updateQuestionInQuiz = useCallback(async (quizId: number, questionId: number, data: UpdateQuizQuestionData) => {
    try {
      setLoading(true);
      setErrorState(null);
      await updateQuestion(quizId, questionId, data);
      setSuccessMessageState('Question updated successfully!');
      // Reload quiz to get updated questions
      await loadQuiz(quizId);
    } catch (err: unknown) {
      console.error('Error updating question:', err);
      const error = err as { response?: { data?: { message?: string; error?: string } } };
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to update question';
      setErrorState(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [loadQuiz]);

  const deleteQuestionFromQuiz = useCallback(async (quizId: number, questionId: number) => {
    try {
      setLoading(true);
      setErrorState(null);
      await deleteQuestion(quizId, questionId);
      setSuccessMessageState('Question deleted successfully!');
      // Reload quiz to get updated questions
      await loadQuiz(quizId);
    } catch (err: unknown) {
      console.error('Error deleting question:', err);
      const error = err as { response?: { data?: { message?: string; error?: string } } };
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to delete question';
      setErrorState(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [loadQuiz]);

  const value: QuizBuilderContextType = {
    quiz,
    loading,
    error,
    successMessage,
    loadQuiz,
    createNewQuiz,
    updateQuizDetails,
    togglePublishStatus,
    addQuestionToQuiz,
    updateQuestionInQuiz,
    deleteQuestionFromQuiz,
    clearError,
    clearSuccessMessage,
    setError,
    setSuccessMessage,
  };

  return (
    <QuizBuilderContext.Provider value={value}>
      {children}
    </QuizBuilderContext.Provider>
  );
};

export const useQuizBuilder = (): QuizBuilderContextType => {
  const context = useContext(QuizBuilderContext);
  if (context === undefined) {
    throw new Error('useQuizBuilder must be used within a QuizBuilderProvider');
  }
  return context;
};

export default QuizBuilderContext;
